import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Sparkles } from "lucide-react";

const DesktopPredictionBar = ({
  selectionCount,
  usageState,
  isLoading,
  error,
  refundNotice,
  onPredict,
  onClear,
  selectedGames = [],
  t,
}) => {
  if (selectionCount === 0 && !error && !refundNotice) return null;

  const canPredict = usageState?.canUse && selectionCount > 0 && !isLoading;
  const totalCoins = selectionCount * 7;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-black/95 border-t border-white/10 py-4 px-6"
      >
        {error && (
          <div className="max-w-7xl mx-auto mb-3 p-3 bg-red-500/10 rounded-sm border border-red-500/30">
            <p className="text-xs text-red-200">{error}</p>
          </div>
        )}

        {refundNotice && (
          <div className="max-w-7xl mx-auto mb-3 p-3 bg-yellow-500/10 rounded-sm border border-yellow-500/30">
            <p className="text-xs text-yellow-200 font-semibold">💰 {refundNotice}</p>
          </div>
        )}

        <div className="max-w-7xl mx-auto flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm font-bold text-white">
                {selectionCount} {selectionCount === 1 ? t?.('Game Selected') || 'Game Selected' : t?.('Games Selected') || 'Games Selected'}
              </p>
              <p className="text-xs text-white/50">
                7 {t?.('coins/game') || 'coins/game'} • {totalCoins} {t?.('coins total') || 'coins total'}
              </p>
            </div>
          </div>

          {selectedGames?.length > 0 && (
            <div className="hidden md:flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Picks:</span>
              {selectedGames.slice(0, 4).map((game, index) => {
                const home = game.teams?.home?.code || game.teams?.home?.name?.slice(0, 3)?.toUpperCase() || 'HOME';
                const away = game.teams?.away?.code || game.teams?.away?.name?.slice(0, 3)?.toUpperCase() || 'AWAY';
                return (
                  <span key={game.id || index} className="text-[10px] font-mono px-2 py-0.5 border border-white/10 rounded-sm text-white/70">
                    {away}@{home}
                  </span>
                );
              })}
              {selectedGames.length > 4 && (
                <span className="text-[10px] font-mono px-2 py-0.5 border border-white/10 rounded-sm text-white/70">
                  +{selectedGames.length - 4}
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={onClear}
              className="px-4 py-2 text-sm font-semibold text-white/60 hover:text-white border border-white/10 rounded-sm"
            >
              {t?.('Clear') || 'Clear'}
            </button>
            <button
              onClick={onPredict}
              disabled={!canPredict}
              className={`flex items-center gap-2 px-6 py-2 rounded-sm text-sm font-semibold transition-colors border ${
                canPredict
                  ? 'border-white/20 text-white hover:border-white/60'
                  : 'border-white/5 text-white/30 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t?.('Analyzing...') || 'Analyzing...'}
                </>
              ) : (
                <>
                  {t?.('Predict Winners') || 'Predict Winners'}
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DesktopPredictionBar;
